import { useQuery } from '@apollo/client';

import { Badge } from '@ovComponents/2-component/badge/badge';

import { UserContext } from '@providers/userContextProvider';
import type { Theme } from 'hephaestus/core';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import { useContext, useMemo, useState } from 'react';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { FETCH_UPCOMING_TRANSFERS } from '../queries';
import {
  BOLD_SX,
  BORDER_RIGHT,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  TRUNCATE_SX,
} from '../styles';

const relDate = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `${diffDays}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fullDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const urgencyBucket = (
  dateStr: string | null,
): '24h' | '7d' | '30d' | 'later' => {
  if (!dateStr) return '30d';
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
  if (diffDays <= 1) return '24h';
  if (diffDays <= 7) return '7d';
  if (diffDays <= 30) return '30d';
  return 'later';
};

const FREQ_MAP: Record<string, string> = {
  ONE_TIME: 'One-time',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_MONTHLY: 'Bi-monthly',
  WEEKLY: 'Weekly',
  BI_WEEKLY: 'Bi-weekly',
  ANNUALLY: 'Annual',
};

const FREQ_SHORT: Record<string, string> = {
  ONE_TIME: '1x',
  MONTHLY: 'Mo',
  QUARTERLY: 'Qtr',
  SEMI_MONTHLY: '2×Mo',
  WEEKLY: 'Wk',
  BI_WEEKLY: '2Wk',
  ANNUALLY: 'Yr',
};

const STATE_DISPLAY: Record<
  string,
  {
    label: string;
    color: 'warning' | 'positive' | 'neutral' | 'negative' | 'supportOne';
  }
> = {
  INITIATED: { label: 'PENDING', color: 'neutral' },
  REVIEWING: { label: 'REVIEW', color: 'warning' },
  PROCESSING: { label: 'PROCESSING', color: 'warning' },
  ACTIVE: { label: 'ACTIVE', color: 'positive' },
  REQUESTED: { label: 'REQUESTED', color: 'warning' },
  COMPLETED: { label: 'DONE', color: 'positive' },
  FAILED: { label: 'FAILED', color: 'negative' },
  CANCELED: { label: 'CANCELED', color: 'negative' },
};

const TYPE_DISPLAY: Record<string, string> = {
  WITHDRAW: 'Withdrawal',
  withdraw: 'Withdrawal',
  DEPOSIT: 'Deposit',
  deposit: 'Deposit',
  TRANSFER: 'Internal Transfer',
  transfer: 'Internal Transfer',
  EFT: 'EFT',
  eft: 'EFT',
};

type NormalizedFlow = {
  id: string;
  direction: 'INFLOW' | 'OUTFLOW';
  amountCents: number;
  type: string;
  source: string;
  clientName: string;
  accountType: string;
  goalName: string;
  state: string;
  frequency: string;
  scheduledDate: string | null;
  createdAt: string;
  currency: string;
  isRecurring: boolean;
  urgency: '24h' | '7d' | '30d' | 'later';
  withdrawalReason: string | null;
  portfolioMvCents: number;
  portfolioCurrency: string;
  atRisk: boolean;
  subAccountId: string;
  fromSubAccountId: string;
};

type PendingCashFlowsProps = {
  compact?: boolean;
};

const PendingCashFlows = ({ compact }: PendingCashFlowsProps) => {
  const formatMoney = useFormatMoney();
  const fmt = (cents: number) =>
    formatMoney(cents, {
      decimals: Math.abs(cents) >= 100_000_00 ? 0 : 2,
    });
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const [urgencyFilter, setUrgencyFilter] = useState<'ALL' | '7d' | '30d'>(
    'ALL',
  );
  const [directionFilter, setDirectionFilter] = useState<
    'ALL' | 'INFLOW' | 'OUTFLOW'
  >('ALL');
  const [recurrenceFilter, setRecurrenceFilter] = useState<
    'ALL' | 'ONE_TIME' | 'RECURRING'
  >('ALL');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, loading } = useQuery(FETCH_UPCOMING_TRANSFERS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          perPage: 200,
          sortField: 'scheduledDate',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
    fetchPolicy: 'cache-first',
  });

  const flows: NormalizedFlow[] = useMemo(() => {
    const transfers = data?.fetchUpcomingTransfers?.upcomingTransfers || [];
    const mapped = transfers.map((t: any) => {
      const isOutflow = t.type === 'WITHDRAW' || t.type === 'withdraw';
      const acct = t.subAccount?.account || t.fromSubAccount?.account;
      const fullName = [acct?.user?.firstName, acct?.user?.lastName]
        .filter(Boolean)
        .join(' ');
      const mvCents = t.subAccount?.statistics?.marketValueCents || 0;
      const mvCurrency = t.subAccount?.statistics?.currency || '';
      const urg = urgencyBucket(t.scheduledDate);
      return {
        id: t.id,
        direction: isOutflow ? ('OUTFLOW' as const) : ('INFLOW' as const),
        amountCents: t.amountCents || 0,
        type: t.type || '',
        source: t.source || '',
        clientName: fullName || '—',
        accountType: acct?.type || '',
        goalName:
          t.subAccount?.goal?.name || t.fromSubAccount?.goal?.name || '',
        state: t.state || '',
        frequency: t.frequency || 'ONE_TIME',
        scheduledDate: t.scheduledDate || null,
        createdAt: t.createdAt || '',
        currency: t.currency || '',
        isRecurring: !!t.isRecurring,
        urgency: urg,
        withdrawalReason: t.withdrawalReason || null,
        portfolioMvCents: mvCents,
        portfolioCurrency: mvCurrency,
        atRisk: isOutflow && t.amountCents > mvCents * 0.9,
        subAccountId: t.subAccount?.id || '',
        fromSubAccountId: t.fromSubAccount?.id || '',
      };
    });
    mapped.sort((a: NormalizedFlow, b: NormalizedFlow) => {
      if (a.scheduledDate && b.scheduledDate)
        return (
          new Date(a.scheduledDate).getTime() -
          new Date(b.scheduledDate).getTime()
        );

      if (a.scheduledDate && !b.scheduledDate) return -1;
      if (!a.scheduledDate && b.scheduledDate) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return mapped;
  }, [data]);

  const filtered = useMemo(
    () =>
      flows.filter((f) => {
        const urgencyOk =
          urgencyFilter === 'ALL' ||
          (urgencyFilter === '7d' &&
            (f.urgency === '24h' || f.urgency === '7d')) ||
          (urgencyFilter === '30d' &&
            (f.urgency === '24h' || f.urgency === '7d' || f.urgency === '30d'));
        const dirOk =
          directionFilter === 'ALL' || f.direction === directionFilter;
        const recOk =
          recurrenceFilter === 'ALL' ||
          (recurrenceFilter === 'RECURRING' ? f.isRecurring : !f.isRecurring);
        const riskOk = !atRiskOnly || f.atRisk;
        return urgencyOk && dirOk && recOk && riskOk;
      }),
    [flows, urgencyFilter, directionFilter, recurrenceFilter, atRiskOnly],
  );

  const stats = useMemo(() => {
    const inflows = flows.filter((f) => f.direction === 'INFLOW');
    const outflows = flows.filter((f) => f.direction === 'OUTFLOW');
    const next7d = flows.filter(
      (f) => f.urgency === '24h' || f.urgency === '7d',
    );
    const atRisk = flows.filter((f) => f.atRisk);
    return {
      totalCount: flows.length,
      inflowCount: inflows.length,
      inflowCents: inflows.reduce((s, f) => s + f.amountCents, 0),
      outflowCount: outflows.length,
      outflowCents: outflows.reduce((s, f) => s + f.amountCents, 0),
      next7dCount: next7d.length,
      atRiskCount: atRisk.length,
      atRiskCents: atRisk.reduce((s, f) => s + f.amountCents, 0),
    };
  }, [flows]);

  if (loading)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size='small' />
        <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
          Loading cash activity…
        </Typography>
      </Box>
    );

  if (flows.length === 0)
    return (
      <Box
        sx={{
          p: compact ? 1.5 : 2,
        }}
      >
        <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
          No pending cash activity for this organization.
        </Typography>
      </Box>
    );

  const isMetricActive = (label: string) =>
    (label === 'Total' &&
      urgencyFilter === 'ALL' &&
      directionFilter === 'ALL' &&
      recurrenceFilter === 'ALL' &&
      !atRiskOnly) ||
    (label === 'Inflows' && directionFilter === 'INFLOW') ||
    (label === 'Outflows' && directionFilter === 'OUTFLOW') ||
    (label === 'Next 7d' && urgencyFilter === '7d') ||
    (label === 'At Risk' && atRiskOnly);

  const statItems = [
    {
      label: 'Total',
      value: `${stats.totalCount}`,
      onClick: () => {
        setUrgencyFilter('ALL');
        setDirectionFilter('ALL');
        setRecurrenceFilter('ALL');
        setAtRiskOnly(false);
      },
    },
    {
      label: 'Inflows',
      value: fmt(stats.inflowCents),
      sub: `${stats.inflowCount} transfers`,
      color: theme.color.positive,
      onClick: () =>
        setDirectionFilter(directionFilter === 'INFLOW' ? 'ALL' : 'INFLOW'),
    },
    {
      label: 'Outflows',
      value: fmt(stats.outflowCents),
      sub: `${stats.outflowCount} transfers`,
      onClick: () =>
        setDirectionFilter(directionFilter === 'OUTFLOW' ? 'ALL' : 'OUTFLOW'),
    },
    {
      label: 'Next 7d',
      value: `${stats.next7dCount}`,
      color: stats.next7dCount > 0 ? theme.color.warning : undefined,
      onClick: () => setUrgencyFilter(urgencyFilter === '7d' ? 'ALL' : '7d'),
    },
    ...(stats.atRiskCount > 0
      ? [
          {
            label: 'At Risk',
            value: `${stats.atRiskCount}`,
            sub: fmt(stats.atRiskCents),
            color: theme.color.warning,
            onClick: () => {
              setAtRiskOnly(!atRiskOnly);
              if (!atRiskOnly) {
                setDirectionFilter('ALL');
                setUrgencyFilter('ALL');
                setRecurrenceFilter('ALL');
              }
            },
          },
        ]
      : []),
  ];

  const FILTER_BTN_SX = {
    px: 0.75,
    py: 0.15,
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid',
    transition: 'all 0.12s',
    '&:hover': { bgcolor: theme.color.neutralHover },
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid',
          borderColor: theme.color.outlineVariant,
        }}
      >
        {statItems.map((stat: any) => (
          <Box
            key={stat.label}
            onClick={stat.onClick}
            sx={{
              flex: 1,
              py: 0.5,
              px: 1,
              cursor: 'pointer',
              ...BORDER_RIGHT,
              '&:last-child': { borderRight: 'none' },
              '&:hover': { bgcolor: theme.color.neutralHover },

              bgcolor: isMetricActive(stat.label)
                ? theme.color.primaryVariant
                : theme.color.background,
            }}
          >
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ ...SECTION_HEADER_SX }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant='bodySmall'
              component='div'
              sx={{ ...BOLD_SX, color: stat.color || theme.color.onSurface }}
            >
              {stat.value}
            </Typography>
            {stat.sub && (
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ color: stat.color || theme.color.onSurfaceVariant }}
              >
                {stat.sub}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: theme.color.outlineVariant,
          bgcolor: theme.color.background,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: theme.color.onDisabled, fontWeight: 600, mr: 0.25 }}
          >
            Direction
          </Typography>
          {(['ALL', 'INFLOW', 'OUTFLOW'] as const).map((val) => {
            const active = directionFilter === val;
            return (
              <Box
                key={val}
                onClick={() => setDirectionFilter(val)}
                sx={{
                  ...FILTER_BTN_SX,
                  bgcolor: active ? theme.color.primaryVariant : 'transparent',
                  borderColor: active
                    ? theme.color.onSurfaceVariant
                    : theme.color.outlineVariant,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    fontWeight: active ? 700 : 400,
                    color: active
                      ? theme.color.onSurface
                      : theme.color.onSurfaceVariant,
                  }}
                >
                  {val === 'ALL' ? 'All' : val === 'INFLOW' ? 'In' : 'Out'}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{ width: '1px', height: 16, bgcolor: theme.color.outlineVariant }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: theme.color.onDisabled, fontWeight: 600, mr: 0.25 }}
          >
            Type
          </Typography>
          {(['ALL', 'ONE_TIME', 'RECURRING'] as const).map((val) => {
            const active = recurrenceFilter === val;
            return (
              <Box
                key={val}
                onClick={() => setRecurrenceFilter(val)}
                sx={{
                  ...FILTER_BTN_SX,
                  bgcolor: active ? theme.color.primaryVariant : 'transparent',
                  borderColor: active
                    ? theme.color.onSurfaceVariant
                    : theme.color.outlineVariant,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    fontWeight: active ? 700 : 400,
                    color: active
                      ? theme.color.onSurface
                      : theme.color.onSurfaceVariant,
                  }}
                >
                  {val === 'ALL'
                    ? 'All'
                    : val === 'RECURRING'
                      ? 'Recurring'
                      : 'One-time'}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {(directionFilter !== 'ALL' ||
          recurrenceFilter !== 'ALL' ||
          urgencyFilter !== 'ALL' ||
          atRiskOnly) && (
          <>
            <Box
              sx={{
                width: '1px',
                height: 16,
                bgcolor: theme.color.outlineVariant,
              }}
            />

            <Typography
              variant='labelSmall'
              onClick={() => {
                setDirectionFilter('ALL');
                setRecurrenceFilter('ALL');
                setUrgencyFilter('ALL');
                setAtRiskOnly(false);
              }}
              sx={{
                color: theme.color.primary,
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Clear filters
            </Typography>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '28px 50px 36px 1fr 100px 80px 40px',
          gap: 0,
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: theme.color.outlineVariant,
          bgcolor: theme.color.background,
        }}
      >
        <Box />
        {['When', 'Dir', 'Client / Account', 'Amount', 'Status', 'Freq'].map(
          (h) => (
            <Typography
              key={h}
              variant='labelSmall'
              sx={{ ...SECTION_HEADER_SX }}
            >
              {h}
            </Typography>
          ),
        )}
      </Box>

      <Box sx={{ maxHeight: compact ? 280 : 400, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
              No transfers match current filters
            </Typography>
          </Box>
        ) : (
          filtered.map((f) => (
            <CashFlowRow
              key={f.id}
              flow={f}
              expanded={expandedId === f.id}
              onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
              theme={theme as any}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

const CashFlowRow = ({
  flow: f,
  expanded,
  onToggle,
  theme,
}: {
  flow: NormalizedFlow;
  expanded: boolean;
  onToggle: () => void;
  theme: Theme;
}) => {
  const formatMoney = useFormatMoney();
  const fmt = (cents: number) =>
    formatMoney(cents, { decimals: Math.abs(cents) >= 100_000_00 ? 0 : 2 });
  const stateDisplay = STATE_DISPLAY[f.state] || {
    label: f.state,
    color: 'neutral' as const,
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: theme.color.outlineVariant,
        borderLeft: f.atRisk ? '3px solid' : '3px solid transparent',
        borderLeftColor: f.atRisk ? theme.color.warning : 'transparent',
        bgcolor: expanded ? theme.color.primaryVariant : 'transparent',
        '&:hover': { bgcolor: theme.color.neutralHover },
        transition: 'background-color 0.12s',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'grid',
          gridTemplateColumns: '28px 50px 36px 1fr 100px 80px 40px',
          gap: 0,
          px: 1.5,
          py: 0.4,
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <IconButton aria-label='collapse' onClick={onToggle} sx={{ p: 0 }}>
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 16 }} fontSize='inherit' />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} fontSize='inherit' />
          )}
        </IconButton>
        <Typography
          variant='labelSmall'
          sx={{
            fontWeight: f.urgency === '24h' ? 700 : 400,
            color:
              f.urgency === '24h' ? theme.color.warning : theme.color.onSurface,
          }}
        >
          {f.scheduledDate ? relDate(f.scheduledDate) : 'Pending'}
        </Typography>
        <Badge
          label={f.direction === 'INFLOW' ? 'IN' : 'OUT'}
          color={f.direction === 'INFLOW' ? 'positive' : 'neutral'}
        />

        <Box
          sx={{
            overflow: 'hidden',
          }}
        >
          <Typography
            variant='labelSmall'
            component='div'
            sx={{ fontWeight: 600, ...TRUNCATE_SX }}
          >
            {f.clientName}
          </Typography>
          <Typography
            variant='labelSmall'
            component='div'
            sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
          >
            {[f.accountType, f.goalName].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Typography
          variant='labelSmall'
          sx={{
            fontWeight: 600,
            color:
              f.direction === 'INFLOW'
                ? theme.color.positive
                : theme.color.onSurface,
          }}
        >
          {f.direction === 'OUTFLOW' ? '−' : '+'}
          {fmt(f.amountCents)} {f.currency}
        </Typography>
        <Badge label={stateDisplay.label} color={stateDisplay.color} />
        <Typography
          variant='labelSmall'
          sx={{
            color: f.isRecurring
              ? theme.color.onSurface
              : theme.color.onSurfaceVariant,
            fontWeight: f.isRecurring ? 600 : 400,
          }}
        >
          {FREQ_SHORT[f.frequency] || f.frequency?.slice(0, 4) || '—'}
        </Typography>
      </Box>

      {expanded && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            mx: 1.5,
            mb: 1,
            bgcolor: theme.color.surface,
            borderRadius: 1,
            border: '1px solid',
            borderColor: theme.color.outlineVariant,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Transfer Details
              </Typography>
              <DetailItem
                theme={theme as any}
                label='Type'
                value={TYPE_DISPLAY[f.type] || f.type}
              />

              <DetailItem
                theme={theme as any}
                label='Direction'
                value={
                  f.direction === 'INFLOW'
                    ? 'Inflow (Deposit)'
                    : 'Outflow (Withdrawal)'
                }
              />

              <DetailItem
                theme={theme as any}
                label='Amount'
                value={`${fmt(f.amountCents)} ${f.currency}`}
                bold
              />

              {f.withdrawalReason && (
                <DetailItem
                  theme={theme as any}
                  label='Reason'
                  value={f.withdrawalReason}
                />
              )}
              {f.source && (
                <DetailItem
                  theme={theme as any}
                  label='Source'
                  value={f.source}
                />
              )}
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Schedule
              </Typography>
              <DetailItem
                theme={theme as any}
                label='Scheduled'
                value={fullDate(f.scheduledDate)}
              />

              <DetailItem
                theme={theme as any}
                label='Frequency'
                value={FREQ_MAP[f.frequency] || f.frequency}
              />

              <DetailItem
                theme={theme as any}
                label='Recurring'
                value={f.isRecurring ? 'Yes' : 'No'}
              />

              <DetailItem
                theme={theme as any}
                label='Created'
                value={fullDate(f.createdAt)}
              />

              <DetailItem
                theme={theme as any}
                label='Status'
                value={stateDisplay.label}
              />
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Account & Risk
              </Typography>
              <DetailItem
                theme={theme as any}
                label='Client'
                value={f.clientName}
                bold
              />

              <DetailItem
                theme={theme as any}
                label='Account'
                value={f.accountType || '—'}
              />

              {f.goalName && (
                <DetailItem
                  theme={theme as any}
                  label='Goal'
                  value={f.goalName}
                />
              )}
              {f.portfolioMvCents > 0 && (
                <>
                  <DetailItem
                    theme={theme as any}
                    label='Portfolio Value'
                    value={`${fmt(f.portfolioMvCents)} ${f.portfolioCurrency}`}
                  />

                  <DetailItem
                    theme={theme as any}
                    label='% of Portfolio'
                    value={`${((f.amountCents / f.portfolioMvCents) * 100).toFixed(1)}%`}
                    color={
                      f.amountCents / f.portfolioMvCents > 0.5
                        ? theme.color.warning
                        : undefined
                    }
                  />
                </>
              )}
              {f.atRisk && (
                <Box
                  sx={{
                    mt: 0.75,
                    px: 1,
                    py: 0.5,
                    bgcolor: theme.color.neutralHover,
                    borderRadius: 0.5,
                    borderLeft: '3px solid',
                    borderLeftColor: theme.color.warning,
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{ color: theme.color.warning, fontWeight: 600 }}
                  >
                    Outflow exceeds 90% of portfolio value
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DetailItem = ({
  label,
  value,
  bold,
  color,
  theme,
}: {
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
  theme: Theme;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      mb: 0.25,
    }}
  >
    <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
      {label}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={{ ...(bold ? BOLD_SX : {}), color: color ?? theme.color.onSurface }}
    >
      {value}
    </Typography>
  </Box>
);

export default PendingCashFlows;
